import { useCallback, useEffect, useRef, useState } from 'react';

import { Outlet, useLocation } from 'react-router-dom';

import ReportDrawerDetailsPage from './components/report-center/Reports/report-drawer-details-page';

import { useAnalytics } from './shared/analytics/create-events';
import { useStore } from './components/state/zustand-store';
import {
  getAllSharedDrivesForDomainQuery,
  UserKeys,
  userSettingsQuery,
} from './api/users-api';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { isAbleToSeeUIElement } from './utils/roles/client-roles';
import { DriveRoles } from './utils/roles/client-roles';
import { isFeatureEnabled } from './utils/feature-flags';
import { posthog } from 'posthog-js';
import { auth } from './shared/firebase/firebase-config';
import useFirebaseKeepAlive from './shared/firebase/useFirebaseKeepAlive';
import useFirebaseChannelSetup from './shared/firebase/useFirebaseChannelSetup';
import {
  FirebaseMessage,
  FirebaseMessageType,
  FBNotificationMessage,
  Page,
  UserListEntity,
} from '../types';
import useFirebaseMessageHandler from './shared/firebase/useFirebaseMessageHandler';
import { userPortalPagesConfig } from './shared/user-portal-pages-config';
import SessionTimeoutDialog from './session-timeout-dialog';
import { GPanelContactsKeys } from './components/directory/gpanel-contacts-v2/gpanel-contacts-api.ts';
import PolicyDetailsDrawerPage from './components/policies/policy-details-drawer-page';
import SignatureTemplateDrawerDetailsPage from './components/automation/signature-templates/signature-template-drawer-details-page';
import DecomPolicyDrawerDetailsPage from './components/automation/decommissioning/decom-policy-drawer-details-page.tsx';
import BulkOpsDrawerDetailsPage from './components/import-export/bulk-operations/bulk-ops-drawer-details-page';
import SupportAgentGate from './components/support-agent/support-agent-gate';
import { CommandDashboardKeys } from './api/command-dashboard-api';

// Command Dashboard tile refreshes send one message: this layout shows it as a notification and
// invalidates TaskStatuses so the tile hook can refetch metrics when RUNNING clears.
const COMMAND_DASHBOARD_TILE_MESSAGE_TYPES: FirebaseMessageType[] = [
  'loginActivityFinished',
  'groupsAndAccessFinished',
];

export default function RoutesLayout() {
  const previousPath = useRef('');
  const isGroupIdentified = useRef(false);
  const hasPrefetchedSharedDrives = useRef(false);
  const location = useLocation();
  const { createPageViewEvent } = useAnalytics();
  const {
    colorMode,
    setColorMode,
    firebaseUser,
    notifications,
    setNotifications,
    setUserIndexingIsDisabled,
    setGroupIndexingIsDisabled,
    setCalendarIndexingIsDisabled,
    setOrgIndexingIsDisabled,
  } = useStore();
  const queryClient = useQueryClient();
  const [openSessionTimeout, setOpenSessionTimeout] = useState(false);

  const { data, dataUpdatedAt } = useSuspenseQuery(userSettingsQuery());

  const isUserPortal = location.pathname.includes(
    userPortalPagesConfig.userPortal.link
  );

  const canSeeSharedDrives = isAbleToSeeUIElement(
    data.role?.driveRoles || [],
    DriveRoles.ViewSharedDrives.value
  );

  const shouldPrefetchSharedDrive = isFeatureEnabled(
    'prefetchSharedDrives',
    data.features
  );

  // prefetch shared drives
  if (canSeeSharedDrives && shouldPrefetchSharedDrive && !isUserPortal) {
    if (!hasPrefetchedSharedDrives.current) {
      queryClient.ensureQueryData(
        getAllSharedDrivesForDomainQuery({
          staleTime: Infinity,
        })
      );
      hasPrefetchedSharedDrives.current = true;
    }
  }

  // dynamically load HubSpot script if admin dashboard
  // useEffect(() => {
  //   if (data.role?.roleType === 'admin') {
  //     // https://developers.hubspot.com/docs/reference/api/conversations/chat-widget-sdk
  //     window.hsConversationsSettings = {
  //       loadImmediately: false,
  //     };

  //     const script = document.createElement('script');
  //     script.type = 'text/javascript';
  //     script.id = 'hs-script-loader';
  //     script.async = true;
  //     script.defer = true;
  //     script.src = '//js.hs-scripts.com/22148155.js';
  //     document.head.appendChild(script);

  //     const style = document.createElement('style');
  //     style.textContent =
  //       '#hubspot-messages-iframe-container { color-scheme: light; }';
  //     document.head.appendChild(style);

  //     return () => {
  //       // Cleanup if needed
  //       const existingScript = document.getElementById('hs-script-loader');
  //       if (existingScript) {
  //         document.head.removeChild(existingScript);
  //       }

  //       // Remove the style element
  //       if (style.parentNode) {
  //         style.parentNode.removeChild(style);
  //       }
  //     };
  //   }
  // }, []);

  // identify the user by google id in posthog
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        const googleProvider = user.providerData.find(
          (provider) => provider.providerId === 'google.com'
        );

        const googleUserId = googleProvider?.uid;
        if (googleUserId) {
          if (!posthog._isIdentified()) {
            posthog.identify(googleUserId, {
              roleType: data.role?.roleType,
              googleId: googleUserId,
            });
          } else {
            posthog.setPersonProperties({
              roleType: data.role?.roleType,
              googleId: googleUserId,
            });
          }

          if (data.subscription?.customerId && !isGroupIdentified.current) {
            posthog.group('customerId', data.subscription.customerId, {
              tier: data.subscription.gPanelPackage,
              userCount: data.subscription.gPanelUserCount,
              isGabCustomer: data.subscription.gabCustomer,
            });
            isGroupIdentified.current = true;
          }
        }
      }
    });

    return () => unsubscribe();
  }, [
    data.role?.roleType,
    data.subscription?.customerId,
    data.subscription?.gPanelPackage,
    data.subscription?.gPanelUserCount,
    data.subscription?.gabCustomer,
  ]);

  // open session timeout if within five minutes of last access time
  useEffect(() => {
    if (data.lastAccessTime && data.sessionTimeout) {
      const now = new Date().getTime();
      const minutesSinceLastAccess = Math.floor(
        (now - data.lastAccessTime) / (1000 * 60)
      );
      const minutesRemaining = Math.ceil(
        data.sessionTimeout - minutesSinceLastAccess
      );
      if (minutesRemaining <= 5 && minutesRemaining > 0) {
        setOpenSessionTimeout(true);
      }
    }
  }, [data.lastAccessTime, data.sessionTimeout, dataUpdatedAt]);

  // track page views
  useEffect(() => {
    if (previousPath.current !== location.pathname) {
      createPageViewEvent();
      previousPath.current = location.pathname;
    }
  }, [createPageViewEvent, location]);

  // set color mode
  useEffect(() => {
    if (colorMode !== data.viewType?.colorMode) {
      setColorMode(data.viewType?.colorMode || 'light');
    }
  }, [colorMode, setColorMode, data.viewType?.colorMode]);

  // setup firebase channel
  useFirebaseChannelSetup();
  useFirebaseKeepAlive(firebaseUser);

  // invalidate queries and update notifications
  const handleMessage = useCallback(
    (message: FirebaseMessage) => {
      const parsedNotification = JSON.parse(
        message.message
      ) as FBNotificationMessage;
      // Command Dashboard tiles refetch from the TaskStatuses RUNNING -> idle transition, so
      // these messages only need that invalidate plus the notification below. Blanking the
      // action keeps them out of the substring matching, which would otherwise refetch
      // unrelated modules that happen to share a word with the tile name.
      const action = COMMAND_DASHBOARD_TILE_MESSAGE_TYPES.includes(message.type)
        ? ''
        : parsedNotification.action.toLowerCase();

      if (COMMAND_DASHBOARD_TILE_MESSAGE_TYPES.includes(message.type)) {
        queryClient.invalidateQueries({
          queryKey: [CommandDashboardKeys.TaskStatuses],
        });
      }

      if (action.includes('user') || action.includes('alias')) {
        const keys = [
          UserKeys.User,
          UserKeys.UserList,
          UserKeys.UserGroups,
          UserKeys.UserRole,
          UserKeys.UserStorage,
          UserKeys.UsersIndexedPage,
          UserKeys.AllUsersCount,
        ];

        keys.forEach((key) => {
          queryClient.invalidateQueries({
            queryKey: [key],
          });
        });
      }

      if (action.includes('group')) {
        const keys = [
          UserKeys.GroupCount,
          UserKeys.GroupMembers,
          UserKeys.GroupMembers,
          UserKeys.GroupSettings,
          UserKeys.GroupTemplates,
          UserKeys.Groups,
          UserKeys.UserGroups,
          UserKeys.GroupSecuritySettings,
          UserKeys.GroupLabels,
          UserKeys.GroupMembersCloudIdentity,
          UserKeys.GroupsIndexedPage,
          UserKeys.AllGroupsCount,
        ];

        keys.forEach((key) => {
          queryClient.invalidateQueries({
            queryKey: [key],
          });
        });
      }

      if (action.includes('calendar')) {
        const keys = [
          UserKeys.CalendarAcls,
          UserKeys.CalendarAppLogs,
          UserKeys.CalendarEvents,
          UserKeys.CalendarEvent,
          UserKeys.CalendarResourceTemplates,
          UserKeys.CalendarResourceTemplatesCount,
          UserKeys.CalendarSearch,
          UserKeys.Calendars,
          UserKeys.CalendarsTransferLogs,
          UserKeys.ResourceCalendarAcls,
          UserKeys.ResourceCalendars,
        ];

        keys.forEach((key) => {
          queryClient.invalidateQueries({
            queryKey: [key],
          });
        });
      }

      if (action.includes('drive')) {
        const keys = [
          UserKeys.DriveAcls,
          UserKeys.DriveDocuments,
          UserKeys.DriveFolder,
          UserKeys.DriveFolders,
          UserKeys.DrivePermissions,
          UserKeys.DriveRootId,
          UserKeys.DriveSweepModels,
          UserKeys.DriveSweepLogs,
          UserKeys.DriveTransferLogs,
          UserKeys.SharedDriveDocuments,
          UserKeys.SharedDriveFolders,
          UserKeys.SharedDriveUserPermission,
          UserKeys.SharedDrivesForDomain,
          UserKeys.SharedDrivesForUser,
          UserKeys.SharedDrivesIndexedPage,
        ];

        keys.forEach((key) => {
          queryClient.invalidateQueries({
            queryKey: [key],
          });
        });
      }

      if (action.includes('org')) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.OrgUnitsFromGoogle],
        });

        queryClient.invalidateQueries({
          queryKey: [UserKeys.OrgUnitModels],
        });
      }

      if (action.includes('contact')) {
        const keys = [
          UserKeys.Contact,
          UserKeys.ContactGroup,
          UserKeys.ContactListModel,
          UserKeys.ContactListModels,
          UserKeys.ContactSyncGroupLabels,
          UserKeys.ContactSyncRunLogs,
          UserKeys.ContactSyncUserModels,
          UserKeys.ContactSyncs,
          UserKeys.Contacts,
          UserKeys.SharedContacts,
        ];

        keys.forEach((key) => {
          queryClient.invalidateQueries({
            queryKey: [key],
          });
        });
      }

      if (action.includes('label')) {
        const keys = [
          UserKeys.Labels,
          UserKeys.GmailLabels,
          UserKeys.ContactSyncGroupLabels,
        ];

        keys.forEach((key) => {
          queryClient.invalidateQueries({
            queryKey: [key],
          });
        });
      }

      if (action.includes('imap')) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.ImapSettings],
        });
      }

      if (action.includes('pop')) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.GmailPopSettings],
        });
      }

      if (action.includes('export')) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.BulkOps],
        });
      }

      if (action.includes('ran a gpanel contacts import')) {
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.OrganizationDirectoryContacts],
        });
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.LabelGroups],
        });
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.LabelContacts],
        });
      }

      if (action.includes('contacts sync')) {
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.AllSyncs],
        });
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.LabelGroups],
        });
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.SyncUsersStatus],
        });
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.LabelSyncs],
        });
      }

      if (action.includes('contact sync task to delete')) {
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.AllSyncs],
        });
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.LabelGroups],
        });
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.SyncUsersStatus],
        });
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.OrganizationDirectoryContacts],
        });
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.TrashContacts],
        });
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.LabelSyncs],
        });
      }

      if (action.includes('mobile')) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.MobileDevicesIndexedPage],
        });
      }

      if (action.includes('report') || action.includes('exported')) {
        queryClient.invalidateQueries({ queryKey: [UserKeys.Reports] });
      }

      if (action.includes('policy')) {
        queryClient.invalidateQueries({ queryKey: [UserKeys.Policies] });
      }

      setNotifications([
        {
          id: message.id,
          title: '',
          message: parsedNotification.action,
          severity: 'info',
          date: message.id,
          read: false,
          docLink: message.docLink ?? null,
        },
        ...notifications,
      ]);
    },
    [notifications, queryClient, setNotifications]
  );

  useFirebaseMessageHandler(
    ['notifications_applog', ...COMMAND_DASHBOARD_TILE_MESSAGE_TYPES],
    firebaseUser.firebaseDbPath,
    handleMessage
  );

  const handleIndexingMessages = useCallback(
    (message: FirebaseMessage) => {
      if (message.type === 'userIndexStarted') {
        setUserIndexingIsDisabled(true);
      } else if (message.type === 'userIndexFinished') {
        setUserIndexingIsDisabled(false);
        queryClient.invalidateQueries({ queryKey: [UserKeys.IndexUserLog] });
        queryClient.invalidateQueries({
          queryKey: [UserKeys.UserCountByDomain],
        });
        queryClient.invalidateQueries({ queryKey: [UserKeys.AllUsersCount] });
      } else if (message.type === 'indexedUserUpdated') {
        interface FirebaseUserList extends UserListEntity {
          isArchived: boolean;
          isSuspended: boolean;
        }

        try {
          const parsedUserListEntity = JSON.parse(
            message.message
          ) as FirebaseUserList;
          //  get all user list queries, if there are any
          const userListQueries = queryClient.getQueriesData<
            Page<UserListEntity[]>
          >({
            queryKey: [UserKeys.UserList],
          });

          if (userListQueries.length > 0) {
            // get the most recent query
            const mostRecentQuery = userListQueries[userListQueries.length - 1];

            const queryKey = mostRecentQuery[0];
            const userList = mostRecentQuery[1];

            if (
              userList?.results.some(
                (user) => user.userEmail === parsedUserListEntity.userEmail
              )
            ) {
              queryClient.setQueryData<Page<UserListEntity[]>>(
                queryKey,
                (oldData) => {
                  if (!oldData) return;

                  const newData = {
                    ...oldData,
                    results: oldData.results.map((user) =>
                      user.userEmail === parsedUserListEntity.userEmail
                        ? {
                            ...parsedUserListEntity,
                            archived: parsedUserListEntity.isArchived,
                            suspended: parsedUserListEntity.isSuspended,
                          }
                        : user
                    ),
                  };
                  return newData;
                }
              );
            }
          }
        } catch {
          // do nothing
        }
      } else if (message.type === 'groupIndexStarted') {
        setGroupIndexingIsDisabled(true);
      } else if (message.type === 'groupIndexFinished') {
        setGroupIndexingIsDisabled(false);
        queryClient.invalidateQueries({ queryKey: [UserKeys.IndexGroupLog] });
        queryClient.invalidateQueries({
          queryKey: [UserKeys.GroupCount],
        });
        queryClient.invalidateQueries({ queryKey: [UserKeys.AllGroupsCount] });
      } else if (message.type === 'calendarIndexStarted') {
        setCalendarIndexingIsDisabled(true);
      } else if (message.type === 'calendarIndexFinished') {
        setCalendarIndexingIsDisabled(false);
        queryClient.invalidateQueries({
          queryKey: [UserKeys.IndexCalendarLog],
        });
        queryClient.invalidateQueries({
          queryKey: [UserKeys.AllCalendarsCount],
        });
      } else if (message.type === 'orgIndexStarted') {
        setOrgIndexingIsDisabled(true);
      } else if (message.type === 'orgIndexFinished') {
        setOrgIndexingIsDisabled(false);
        queryClient.invalidateQueries({ queryKey: [UserKeys.IndexOrgLog] });
        queryClient.invalidateQueries({
          queryKey: [UserKeys.AllOrgsCount],
        });
      }
    },
    [
      queryClient,
      setUserIndexingIsDisabled,
      setGroupIndexingIsDisabled,
      setCalendarIndexingIsDisabled,
      setOrgIndexingIsDisabled,
    ]
  );

  useFirebaseMessageHandler(
    [
      'userIndexStarted',
      'userIndexFinished',
      'groupIndexStarted',
      'groupIndexFinished',
      'calendarIndexStarted',
      'calendarIndexFinished',
      'orgIndexStarted',
      'orgIndexFinished',
      'indexedUserUpdated',
    ],
    firebaseUser.firebaseDbPath,
    handleIndexingMessages
  );

  return (
    <>
      {openSessionTimeout && (
        <SessionTimeoutDialog
          open={openSessionTimeout}
          handleClose={() => setOpenSessionTimeout(false)}
          lastAccessTime={data.lastAccessTime}
          sessionTimeout={data.sessionTimeout}
          dataUpdatedAt={dataUpdatedAt}
        />
      )}
      <PolicyDetailsDrawerPage />
      <SignatureTemplateDrawerDetailsPage />
      <DecomPolicyDrawerDetailsPage />
      <Outlet />
      <ReportDrawerDetailsPage />
      <BulkOpsDrawerDetailsPage />
      <SupportAgentGate features={data.features} />
    </>
  );
}
